import React, { ReactNode } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { HeadingLG, TitleMD, TitleXS } from '@/atoms/Text'
import { GiveawayStep, Maybe } from '@/types/codegen-contentful'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGiveawayContext } from '../../context/GiveawayContext'

interface Props {
  steps: Maybe<GiveawayStep>[]
  className?: string
}

export const HowItWorks: React.FC<Props> = ({ steps, className }) => {
  const { t } = useTranslate('tickets')
  const { scrollToProducts } = useGiveawayContext()

  return (
    <FlexColumn
      className={classNames('mx-auto max-w-[1080px] gap-4 pb-12 pt-[72px] md:pb-16 md:pt-[120px]', className)}
    >
      <HeadingLG className="font-semibold md:font-bold md:uppercase" color="giveaway-text-primary-dark">
        {t('howItWorks', 'How it Works')}
      </HeadingLG>

      {steps.map(
        (step, index) =>
          step && (
            <StepValue key={step.title} step={(index + 1).toString().padStart(2, '0')}>
              <strong>{step.title}</strong>: {step.description}
            </StepValue>
          ),
      )}
      <Button
        variant="core-gray-400"
        className="mt-6 hidden rounded-xl !border-2 !border-giveaway-text-subtle-light py-3.5 !font-medium !text-giveaway-text-subtle-light md:block md:w-fit"
        outline
        onClick={scrollToProducts}
      >
        {t('preOrderTicketsV2', 'Preorder Tickets')}
      </Button>
    </FlexColumn>
  )
}

const StepValue = ({ step, children }: { step: string; children: ReactNode }) => (
  <div className="mb-4 flex items-start justify-start gap-2">
    <div className="flex rounded-md bg-giveaway-primary-subtle px-2 md:px-3">
      <TitleXS
        color="giveaway-text-primary-light"
        weight="semibold"
        className="md:photon-title-sm leading-[26px] md:leading-[31px]"
      >
        {step}
      </TitleXS>
    </div>
    <TitleMD className="md:photon-title-lg !leading-tight" color="giveaway-text-primary-dark" weight="normal">
      {children}
    </TitleMD>
  </div>
)
